import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Select, useScreenWidth } from "@msys/ui";
import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useLatest } from "react-use";
import { Agreement, PermissionName } from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { useAuthenticatedViewer } from "../../auth/useUserData.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageColumn } from "../../commons/layout/PageColumn.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { isTreePreviewItemVisible } from "../../features/doc-items/helpers.js";
import {
  createTreeClientItem,
  createTreeItem,
  createTreeItemInput,
} from "../../features/projects/quote-trees.js";
import { QuoteAnnotationsBanner } from "../../features/quote-annotations/QuoteAnnotationsBanner.js";
import {
  allowHaveChildren,
  shouldRenderCreateInput,
} from "../../features/quotes/helpers.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { useCreateQuoteItems } from "../../features/quotes/useCreateQuoteItems.js";
import { WizardQuestionNavigation } from "../../features/quotes/wizard/WizardQuestionNavigation.js";
import { WizardQuestionShell } from "../../features/quotes/wizard/WizardQuestionShell.js";
import {
  getCurrentQuestion,
  getQuestions,
  RememberedSelection,
  resolveNextStep,
  resolvePreviousStep,
  trackSelection,
} from "../../features/quotes/wizard/questionShell.js";
import { useAnswerWizardQuestion } from "../../features/quotes/wizard/useAnswerWizardQuestion.js";
import { WizardRendererRef } from "../../features/quotes/wizard/WizardRendererProps.js";
import {
  useQuoteWizardQuestionsQuery,
  WizardQuestions_QuestionFragment,
} from "../../features/documents/WizardQuestions.generated.js";
import {
  getNextWizardContext,
  useWizardContextOptions,
  WizardContext,
  wizardContextStrategies,
} from "../../features/documents/useWizardContext.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { VirtualBareTreeStandaloneItem } from "../../trees-virtual/components/VirtualBareTreeStandaloneItem.js";
import { getEliminatedItemsFilter } from "../../trees-virtual/helpers.js";
import { useEliminatedItemsStoreWithLocalStorage } from "../../trees-virtual/hooks/useEliminatedItemsStore.js";
import { useEnrichExpandedStoreWithParentPathIds } from "../../trees-virtual/hooks/useEnrichExpandedStoreWithParentPathIds.js";
import { useExpandedStoreWithLocalStorage } from "../../trees-virtual/hooks/useExpandedStore.js";
import { useMoveTreeItem } from "../../trees-virtual/hooks/useMoveTreeItem.js";
import { TreeViewMode } from "../../trees-virtual/types.js";
import { VirtualItemTree } from "../../trees-virtual/VirtualItemTree.js";
import {
  TreeToggleAllExpandedButton,
  TreeToggleEliminatedItemsButton,
} from "../../trees/components/TreeToggleButton.js";
import { falseFn, nullFn } from "../../utils.js";
import {
  ProjectQuoteContext,
  useProjectQuoteContext,
} from "./ProjectQuoteContext.js";
import {
  ProjectQuoteWizard_ItemFragment,
  useProjectQuoteWizardQuery,
} from "./ProjectQuoteWizard.generated.js";

interface Props {
  projectId: string;
  quoteId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export function ProjectQuoteWizard({
  projectId,
  quoteId,
  submenuItems,
  prefixBreadcrumbs,
  activeSubmenuItem,
}: Props) {
  const viewer = useAuthenticatedViewer();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const latestLocation = useLatest(location);

  const pathToDocList = `/projects/${projectId}/quotes`;
  const pathToDoc = `/projects/${projectId}/quotes/${quoteId}`;
  const pathToDocPage = `${pathToDoc}/wizard`;
  const match = useMatch(`${pathToDocPage}/items/:itemId`);
  const selectedItemId = match?.params?.itemId ?? null;
  const latestSelectedItemId = useLatest(selectedItemId);

  const isClientVisibilityEnabled = useFeature("ClientVisibility");
  const { isMinDesktop, isMinTablet } = useScreenWidth();

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    const confirmProcess = confirmProcessRef.current;
    if (!confirmProcess) {
      throw new Error("ConfirmProcess is not mounted");
    }
    return confirmProcess.startConfirmProcess(props);
  }, []);

  const [treeContainer, setTreeContainer] =
    React.useState<HTMLDivElement | null>(null);

  const expandedStore = useExpandedStoreWithLocalStorage(
    "quote",
    quoteId,
    selectedItemId
  );

  const [moveTreeItemRaw, moveTreeItemLoading] = useMoveTreeItem({
    projectId,
    docId: quoteId,
  });

  const client = useApolloClient();
  const query = useProjectQuoteWizardQuery({
    client,
    variables: {
      projectId,
      quoteId,
      expandedItemIds: expandedStore.expandedItemIds,
    },
  });
  const project = getDataOrNull(query.data?.project)?.project;
  const doc = getDataOrNull(query.data?.quote)?.quote;

  const isAgreed =
    (doc?.isPublished && doc?.isBinding && doc?.agreement === "YES") ?? false;
  const isViewersQuote =
    doc?.owningSystemOrganisationId === viewer.organisation.id;

  const { areEliminatedItemsShown, setEliminatedItemsShown } =
    useEliminatedItemsStoreWithLocalStorage("quote", quoteId);

  const [filteredTreeItems, rootItem, allDocItems] = React.useMemo(() => {
    if (!doc) return [[], undefined, []];
    const allDocItems = doc.items;
    const rootItem = allDocItems.find(item => item.isRootItem);
    const treeItems = allDocItems.filter(
      getEliminatedItemsFilter(
        areEliminatedItemsShown,
        selectedItemId,
        allDocItems
      )
    );
    return [treeItems, rootItem, allDocItems];
  }, [doc, areEliminatedItemsShown, selectedItemId]);

  useEnrichExpandedStoreWithParentPathIds(
    expandedStore,
    allDocItems,
    selectedItemId,
    !query.loading && !query.error
  );

  const navigateToItem = React.useCallback(
    (id: string) =>
      navigate(
        {
          pathname: `${pathToDocPage}/items/${id}`,
          search: latestLocation.current.search,
        },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );

  const viewerPermissions: PermissionName[] = React.useMemo(
    () => doc?.viewerPermissions ?? [],
    [doc?.viewerPermissions]
  );
  const agreement: Agreement | undefined = doc?.agreement;
  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const projectQuoteContext = useProjectQuoteContext();
  const {
    activeWizardContext,
    setActiveWizardContext,
    refetchWizardQuestions,
  } = projectQuoteContext;

  const { t } = useTranslate(["QuoteEdit"]);
  const wizardModeOptions = useWizardContextOptions();

  const questionsQuery = useQuoteWizardQuestionsQuery({
    client,
    variables: {
      projectId,
      quoteId,
      strategy: wizardContextStrategies[activeWizardContext],
    },
  });
  const questions = React.useMemo(
    () => getQuestions(questionsQuery.data?.quoteWizardQuestions),
    [questionsQuery.data?.quoteWizardQuestions]
  );

  const refetchQuestions = React.useCallback(async () => {
    const result = await questionsQuery.refetch();
    return getQuestions(result.data?.quoteWizardQuestions);
  }, [questionsQuery]);

  // A tree edit can add, remove or unlock questions, and the list is otherwise
  // only refetched after an answer — so every wizard-affecting mutation on this
  // route refetches it too.
  const refetchWizardQuestionsAndList = React.useCallback(async () => {
    await Promise.all([refetchWizardQuestions(), refetchQuestions()]);
  }, [refetchQuestions, refetchWizardQuestions]);

  const moveTreeItem = React.useCallback(
    async (itemId: string, parentId: string, rank: number) => {
      await moveTreeItemRaw(itemId, parentId, rank);
      await refetchWizardQuestionsAndList();
    },
    [moveTreeItemRaw, refetchWizardQuestionsAndList]
  );

  const {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
    fetchItemRecommendations,
    createItemFromRecommendation,
  } = useCreateQuoteItems({
    projectId,
    quoteId,
    expandedItemIds: expandedStore.expandedItemIds,
    onItemCreated: refetchWizardQuestionsAndList,
    source: "wizard",
  });

  // The URL is the single source of truth for the selection: a tree click and a
  // step both navigate, and the shell derives the current question from the
  // URL — so neither direction can echo the other.
  const currentQuestionId =
    latestLocation.current.hash.replace(/^#/, "") || null;
  const currentQuestion = getCurrentQuestion(
    questions,
    selectedItemId,
    currentQuestionId
  );

  // No selection is the done state, so stepping past the last question lands
  // on the route itself rather than going nowhere.
  const goToQuestion = React.useCallback(
    (target: WizardQuestions_QuestionFragment | null) => {
      navigate(
        target
          ? {
              pathname: `${pathToDocPage}/items/${target.itemId}`,
              search: latestLocation.current.search,
              hash: target.questionId,
            }
          : {
              pathname: pathToDocPage,
              search: latestLocation.current.search,
            },
        { replace: true }
      );
    },
    [latestLocation, navigate, pathToDocPage]
  );

  const [answerQuestion, answering, stepAfterAnswer] = useAnswerWizardQuestion({
    projectId,
    quoteId,
    wizardContext: activeWizardContext,
    questions,
    refetchQuestions,
    onStep: goToQuestion,
  });

  const selectedPathSortable = allDocItems.find(
    i => i.id === selectedItemId
  )?.pathSortable;
  const idle =
    selectedPathSortable === undefined
      ? undefined
      : { pathSortable: selectedPathSortable };
  const nextQuestion = resolveNextStep(
    questions,
    currentQuestion?.questionId ?? null,
    idle
  );
  // From the done state back is the list's last question; from anywhere else
  // forward always leads somewhere, because the done state is a destination.
  const previousQuestion = selectedItemId
    ? resolvePreviousStep(questions, currentQuestion?.questionId ?? null, idle)
    : (questions[questions.length - 1] ?? null);

  // Where the view lands when the URL names no item, resolved once per mode:
  // on arrival the root item, and on a switch the new mode's first question.
  // Running out of questions within a mode is the done state and stays put,
  // which is why this cannot simply key on an empty selection.
  const landedMode = React.useRef<WizardContext | null>(null);
  const loading = query.loading || questionsQuery.loading;
  const hasLoadError = Boolean(query.error || questionsQuery.error);

  React.useEffect(() => {
    if (loading || hasLoadError) return;

    const isArrival = landedMode.current === null;
    const hasSwitchedMode =
      !isArrival && landedMode.current !== activeWizardContext;
    landedMode.current = activeWizardContext;

    // A named item is where the user already is; only the done state moves.
    if (latestSelectedItemId.current) return;

    if (isArrival && rootItem && questions.length > 0) {
      navigateToItem(rootItem.id);
      return;
    }

    if (hasSwitchedMode && questions[0]) goToQuestion(questions[0]);
  }, [
    activeWizardContext,
    goToQuestion,
    hasLoadError,
    latestSelectedItemId,
    loading,
    navigateToItem,
    questions,
    rootItem,
  ]);

  // Losing the selection because its item was deleted steps on from where the
  // item stood; losing it any other way is the done state and stays put.
  const rememberedSelection = React.useRef<RememberedSelection | null>(null);

  React.useEffect(() => {
    const { remembered, deleted: deletedItem } = trackSelection(
      rememberedSelection.current,
      { itemId: selectedItemId, pathSortable: selectedPathSortable },
      itemId => allDocItems.some(i => i.id === itemId)
    );
    rememberedSelection.current = remembered;
    if (!deletedItem) return;

    void Promise.all([refetchWizardQuestions(), refetchQuestions()])
      .then(([, refetched]) =>
        goToQuestion(
          resolveNextStep(refetched, null, {
            pathSortable: deletedItem.pathSortable,
          })
        )
      )
      .catch((error: unknown) => {
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
      });
  }, [
    allDocItems,
    enqueueSnackbar,
    goToQuestion,
    refetchQuestions,
    refetchWizardQuestions,
    selectedItemId,
    selectedPathSortable,
  ]);

  const nextMode = React.useMemo(() => {
    if (!viewerPermissions.includes("quote_decisions_decide")) return null;

    const next = getNextWizardContext(activeWizardContext);
    const option = wizardModeOptions.find(o => o.value === next);

    return next && option
      ? { label: option.label, onSelect: () => setActiveWizardContext(next) }
      : null;
  }, [
    activeWizardContext,
    setActiveWizardContext,
    viewerPermissions,
    wizardModeOptions,
  ]);

  // Set only by a renderer that stages its answer (the property form). The
  // action bar's forward button then submits instead of navigating, so a
  // question never shows a Save next to a Next.
  const stagedRenderer = React.useRef<WizardRendererRef>(null);

  const selectedItem = React.useMemo(() => {
    const item = allDocItems.find(i => i.id === selectedItemId);
    return item ? { id: item.id, title: item.title } : null;
  }, [allDocItems, selectedItemId]);

  /** Nothing is selected and nothing is loading: the mode has nothing left. */
  const isDone = !currentQuestion && !selectedItem && !loading && !hasLoadError;

  const placeholderItem = React.useMemo(
    () => allDocItems.find(i => i.id === currentQuestion?.itemId) ?? null,
    [allDocItems, currentQuestion?.itemId]
  );

  const TreeItem = React.useMemo(() => {
    if (!doc?.id || agreement === undefined) return nullFn;

    return isViewersQuote
      ? createTreeItem({
          pathToDocPage,
          projectId,
          doc: {
            id: doc.id,
            rootItemId: doc.rootItem.id,
            viewerPermissions,
            owningSystemOrganisationId: doc.owningSystemOrganisationId,
          },
          docAgreement: agreement,
          startConfirmProcess,
          expandedItemIds: expandedStore.expandedItemIds,
          setItemExpanded: expandedStore.setItemExpanded,
          pasteItem,
          isAllowedToPasteItem,
          onAdditionalItemInputChange: null,
          getTo: itemProps => ({
            pathname: `${pathToDocPage}/items/${itemProps.item.id}`,
            search: latestLocation.current.search,
          }),
          isItemMuted: falseFn,
        })
      : createTreeClientItem({
          projectId,
          doc: {
            id: doc.id,
            rootItemId: doc.rootItem.id,
            viewerPermissions,
            owningSystemOrganisationId: doc.owningSystemOrganisationId,
          },
          docAgreement: agreement,
          expandedItemIds: expandedStore.expandedItemIds,
        });
  }, [
    doc?.id,
    doc?.rootItem.id,
    doc?.owningSystemOrganisationId,
    agreement,
    isViewersQuote,
    pathToDocPage,
    projectId,
    viewerPermissions,
    startConfirmProcess,
    expandedStore.expandedItemIds,
    expandedStore.setItemExpanded,
    pasteItem,
    isAllowedToPasteItem,
    latestLocation,
  ]);

  const TreeItemInput = React.useMemo(
    () =>
      createTreeItemInput({
        viewMode: TreeViewMode.Item,
        quoteId,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        pasteItem,
        isAllowedToPasteItem,
        fetchItemRecommendations,
        createItemFromRecommendation,
      }),
    [
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      isAllowedToPasteItem,
      pasteItem,
      quoteId,
      fetchItemRecommendations,
      createItemFromRecommendation,
    ]
  );

  const filterItem = React.useCallback(
    (item: ProjectQuoteWizard_ItemFragment) =>
      isTreePreviewItemVisible(true, item, allDocItems, doc?.isBinding ?? true),
    [allDocItems, doc?.isBinding]
  );

  return (
    <ProjectQuoteContext.Provider
      value={React.useMemo(
        () => ({
          ...projectQuoteContext,
          refetchWizardQuestions: refetchWizardQuestionsAndList,
        }),
        [projectQuoteContext, refetchWizardQuestionsAndList]
      )}
    >
      <Page
        subtitle={project?.title}
        title={doc?.title ?? ""}
        submenuItems={submenuItems}
        breadcrumbs={prefixBreadcrumbs}
        header={
          <PageHeader
            breadcrumbs={prefixBreadcrumbs}
            submenuItems={submenuItems}
            activeSubmenuItem={activeSubmenuItem}
          />
        }
        subHeader={
          project && doc ? (
            <QuoteSubHeader
              project={project}
              quote={doc}
              activeView="wizard"
              canEdit={isViewersQuote}
              isReadOnly={isAgreed}
              pathToDocList={pathToDocList}
              pathToDoc={pathToDoc}
              hasSapS4HanaIntegration={hasSapS4HanaIntegration}
              isHeaderVisible={undefined as never}
              setHeaderVisible={undefined as never}
              expandedItemIds={expandedStore.expandedItemIds}
            />
          ) : undefined
        }
        action={
          // The wizard's own two moves, and nothing else: publishing is reached
          // by switching layout view. Rendered from the mount, so the bar does
          // not appear only once the document resolves.
          <WizardQuestionNavigation
            question={currentQuestion}
            isDone={isDone}
            nextMode={nextMode}
            onNext={() => goToQuestion(nextQuestion)}
            onPrevious={() => goToQuestion(previousQuestion)}
            canGoNext={Boolean(selectedItemId)}
            canGoPrevious={Boolean(previousQuestion)}
            busy={answering || loading || hasLoadError}
            stagedRef={stagedRenderer}
          />
        }
      >
        {isMinDesktop && (
          <PageColumn
            $hasRightBorder
            $hasBackground
            $display="block"
            ref={isMinTablet ? setTreeContainer : undefined}
          >
            {doc && project && rootItem && (
              <Stack direction="column" spacing={2}>
                <QuoteAnnotationsBanner />
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ width: "100%", alignItems: "center" }}
                >
                  <TreeToggleAllExpandedButton
                    areAllItemsExpanded={expandedStore.areAllItemsExpanded}
                    setAllItemsExpanded={expandedStore.setAllItemsExpanded}
                  />
                  <TreeToggleEliminatedItemsButton
                    areEliminatedItemsShown={areEliminatedItemsShown}
                    setEliminatedItemsShown={setEliminatedItemsShown}
                  />
                  <RestrictedByDocumentPermissionWithDebug
                    permission="quote_decisions_decide"
                    document={doc}
                  >
                    <Select
                      aria-label={t("Wizard", { ns: "QuoteEdit" })}
                      options={wizardModeOptions}
                      value={activeWizardContext}
                      onChange={value => {
                        setActiveWizardContext(value);
                      }}
                      // A step in flight resolves against the mode it started in.
                      disabled={answering}
                      size="extra-small"
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                  </RestrictedByDocumentPermissionWithDebug>
                </Stack>

                <Box>
                  <VirtualBareTreeStandaloneItem
                    item={rootItem}
                    items={filteredTreeItems}
                    allItems={allDocItems}
                    depth={0}
                    itemComponent={TreeItem}
                    selected={selectedItemId === rootItem.id}
                    sticky={selectedItemId === rootItem.id}
                    top={0}
                    clickable
                    onClick={() => navigateToItem(rootItem.id)}
                  />

                  <VirtualItemTree<ProjectQuoteWizard_ItemFragment>
                    moveTreeItem={
                      isViewersQuote && !isAgreed ? moveTreeItem : undefined
                    }
                    moveTreeItemLoading={moveTreeItemLoading}
                    items={filteredTreeItems}
                    allItems={allDocItems}
                    filterItem={
                      isClientVisibilityEnabled || !isViewersQuote
                        ? filterItem
                        : undefined
                    }
                    selectedItemId={selectedItemId}
                    enableCreating={isViewersQuote}
                    documentItemTypes={QUOTE_ITEM_TYPES}
                    container={treeContainer}
                    itemComponent={TreeItem}
                    inputComponent={isViewersQuote ? TreeItemInput : nullFn}
                    allowHaveChildren={
                      isViewersQuote ? allowHaveChildren : falseFn
                    }
                    shouldRenderCreateInput={
                      isViewersQuote ? shouldRenderCreateInput : falseFn
                    }
                    expandedStore={expandedStore}
                  />
                </Box>
              </Stack>
            )}
          </PageColumn>
        )}
        <ConfirmProcess ref={confirmProcessRef} />
        {/* the `items/:itemId` child route carries the selection and renders nothing */}
        <Outlet />
        <PageContainer $fullHeight>
          <WizardQuestionShell
            projectId={projectId}
            quoteId={quoteId}
            questions={questions}
            question={currentQuestion}
            selectedItem={selectedItem}
            loading={loading}
            loadError={hasLoadError}
            placeholderItem={placeholderItem}
            expandedItemIds={expandedStore.expandedItemIds}
            onAnswer={answerQuestion}
            onApplied={stepAfterAnswer}
            onSelectQuestion={goToQuestion}
            busy={answering}
            navigationLocked={answering || loading || hasLoadError}
            stagedRef={stagedRenderer}
          />
        </PageContainer>
      </Page>
    </ProjectQuoteContext.Provider>
  );
}
